'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface CustomLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  onSkip?: () => void;
  redirectPath?: string;
}

export default function CustomLinkDialog({ 
  open, 
  onOpenChange, 
  onSuccess, 
  onSkip,
  redirectPath = '/orders'
}: CustomLinkDialogProps) {
  const router = useRouter();
  const [customLinkCode, setCustomLinkCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleCreateLink = async () => {
    if (!customLinkCode.trim()) {
      toast.error('Please enter a custom link code');
      return;
    }

    // Basic validation for URL-safe characters
    const linkCodeRegex = /^[a-zA-Z0-9-_]+$/;
    if (!linkCodeRegex.test(customLinkCode)) {
      toast.error('Link code can only contain letters, numbers, hyphens, and underscores');
      return;
    }

    setIsLoading(true);
    try {
      const trimmedCode = customLinkCode.toLowerCase().trim();
      
      // First, check if vendor already has a link
      const checkResponse = await fetch('/api/menu/links');
      
      if (checkResponse.ok) {
        const checkResult = await checkResponse.json();
        if (checkResult.data) {
          // Vendor has a link record, update it instead of creating
          const response = await fetch('/api/menu/links', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              custom_link_code: trimmedCode
            }),
          });

          if (response.ok) {
            toast.success('Custom link created successfully!');
            onOpenChange(false);
            if (onSuccess) {
              onSuccess();
            } else {
              router.push(redirectPath);
            }
            return;
          } else {
            console.error('API Error Response:', response.status, response.statusText);
            try {
              const errorData = await response.json();
              console.error('Error Data:', errorData);
              toast.error(errorData.error || errorData.message || `Failed to create custom link (${response.status})`);
            } catch (parseError) {
              console.error('Failed to parse error response:', parseError);
              toast.error(`Failed to create custom link (${response.status}: ${response.statusText})`);
            }
            setIsLoading(false);
            return;
          }
        }
      }

      // Proceed with creating the link (only if vendor has no link record at all)
      const response = await fetch('/api/menu/links', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          link_code: trimmedCode,
          custom_link_code: trimmedCode
        }),
      });

      if (response.ok) {
        toast.success('Custom link created successfully!');
        onOpenChange(false);
        if (onSuccess) {
          onSuccess();
        } else {
          router.push(redirectPath);
        }
      } else {
        console.error('API Error Response:', response.status, response.statusText);
        try {
          const errorData = await response.json();
          console.error('Error Data:', errorData);
          toast.error(errorData.error || errorData.message || `Failed to create custom link (${response.status})`);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          toast.error(`Failed to create custom link (${response.status}: ${response.statusText})`);
        }
      }
    } catch (error) {
      console.error('Network/Request Error:', error);
      toast.error(`Network error: ${error instanceof Error ? error.message : 'Failed to create custom link'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipLink = () => {
    onOpenChange(false);
    if (onSkip) {
      onSkip();
    } else {
      router.push(redirectPath);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden">
        <div className="flex min-h-[500px]">
          {/* Left side - Image */}
          <div className="w-1/2">
            <Image
              src="https://rgvfkcfnfbtqyjlrvynz.supabase.co/storage/v1/object/public/vendor-assets/vendor-assets/link2x.png"
              alt="Custom Link Illustration"
              width={500}
              height={500}
              className="max-w-full h-auto object-contain"
              priority
            />
          </div>

          {/* Right side - Form */}
          <div className="w-1/2 p-8">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-2xl">Create Your Custom Link</DialogTitle>
              <DialogDescription className="text-xs">
                Create a custom link for your menu page that customers can easily remember and access.
              </DialogDescription>
            </DialogHeader>
            
            {/* Warning about one-time change */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-6">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-amber-700 mt-1">
                    Your custom link can only be changed once.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center space-x-2 bg-muted py-3 px-4 rounded-xl">
                  <span className="text-sm text-muted-foreground font-medium">menumate.in/</span>
                  <Input
                    id="customLink"
                    value={customLinkCode}
                    onChange={(e) => {
                      const value = e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '');
                      if (value.length <= 20) {
                        setCustomLinkCode(value);
                      }
                    }}
                    placeholder="your-caterer-name"
                    maxLength={20}
                    className="flex-1 h-12 text-base"
                  />
                </div>

                <div className="flex justify-between items-center text-xs text-muted-foreground">
                  <span>Only letters, numbers, hyphens, and underscores allowed</span>
                  <span className={customLinkCode.length > 18 ? 'text-orange-500' : ''}>{customLinkCode.length}/20</span>
                </div>
              </div>

              <div className="pt-4">
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={handleCreateLink}
                    variant="green"
                    disabled={isLoading || !customLinkCode.trim()}
                    className="w-full h-12 text-base"
                    size="lg"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Link'
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleSkipLink}
                    disabled={isLoading}
                    className="w-full h-12 text-base"
                  >
                    Skip for now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}